import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, ImagesSquare } from '@phosphor-icons/react';
import {
  Timestamp,
  collection,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore';
import { useAuth } from '@/core/AuthContext';
import { db } from '@/core/firebase';
import { SectionCard } from '../../../components/trainer/SectionCard';
import { StatPill } from '../../../components/trainer/StatPill';
import { StarRating } from '../../../components/ui/StarRating';
import { Button } from '../../../components/ui/Button';
import type { WorkoutSession } from '../../../types';
import { useIsMobile } from '../../../lib/useIsMobile';
import { formatDuration, getDurationMinutes } from '@/shared/utils/dates';
import { formatProgressPhotoDate, getComparePair, getProgressPhotos } from '../api/progressPhotos';
import type { ProgressPhoto } from '../api/progressPhotos';

export function Progress() {
  const { user, loading } = useAuth();
  const [sessions, setSessions] = useState<WorkoutSession[]>([]);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    // Wait for auth to finish loading to avoid permission-denied errors
    if (loading || !user) return;

    const sessionsQuery = query(
      collection(db, 'workoutSessions'),
      where('clientId', '==', user.uid)
    );

    const unsubSessions = onSnapshot(sessionsQuery, (snapshot) => {
      const docs = snapshot.docs.map(
        (docSnap) => ({ id: docSnap.id, ...docSnap.data() } as WorkoutSession)
      );
      setSessions(docs);
    });

    return () => {
      unsubSessions();
    };
  }, [user, loading]);

  const completedSessions = sessions.filter(s => s.status === 'completed');
  const thisWeekSessions = sessions.filter(s => {
    const startOfWeek = new Date();
    startOfWeek.setHours(0, 0, 0, 0);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    const sessionDate = (s.startedAt as Timestamp).toDate();
    return sessionDate >= startOfWeek;
  });

  const thisMonthSessions = sessions.filter(s => {
    const now = new Date();
    const sessionDate = (s.startedAt as Timestamp).toDate();
    return sessionDate.getMonth() === now.getMonth() &&
           sessionDate.getFullYear() === now.getFullYear();
  });

  // Calculate average workout duration for completed sessions
  const averageDuration = useMemo(() => {
    const completedWithDuration = completedSessions.filter(s => s.completedAt);
    if (completedWithDuration.length === 0) return null;
    
    const totalMinutes = completedWithDuration.reduce((sum, session) => {
      return sum + getDurationMinutes(session.startedAt, session.completedAt);
    }, 0);
    
    return Math.round(totalMinutes / completedWithDuration.length);
  }, [completedSessions]);

  // Calculate average workout rating for completed sessions
  const averageRating = useMemo(() => {
    const ratedSessions = completedSessions.filter(s => s.rating && s.rating > 0);
    if (ratedSessions.length === 0) return null;
    
    const totalRating = ratedSessions.reduce((sum, session) => sum + (session.rating || 0), 0);
    return (totalRating / ratedSessions.length).toFixed(1);
  }, [completedSessions]);

  const [progressPhotos, setProgressPhotos] = useState<ProgressPhoto[]>([]);

  const loadPhotos = useCallback(async () => {
    if (!user) {
      setProgressPhotos([]);
      return;
    }
    const loaded = await getProgressPhotos();
    setProgressPhotos(loaded);
  }, [user]);

  useEffect(() => {
    void loadPhotos();
  }, [loadPhotos]);

  const comparePair = useMemo(() => getComparePair(progressPhotos), [progressPhotos]);

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3">
          <div>
            <p className="text-sm text-primary-600 dark:text-primary-400 uppercase tracking-wide font-semibold">
              Progress
            </p>
            <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
              Your Progress
            </h1>
            <p className="text-sm text-gray-600 dark:text-slate-400">
              Track your fitness journey and achievements
            </p>
          </div>
        </div>

        {/* Stats */}
        {!isMobile && (
        <div className="grid sm:grid-cols-6 gap-3">
          <StatPill label="Total Sessions" value={sessions.length} />
          <StatPill label="Completed" value={completedSessions.length} />
          <StatPill label="This Week" value={thisWeekSessions.length} />
          <StatPill label="This Month" value={thisMonthSessions.length} />
          <StatPill 
            label="Avg Duration" 
            value={averageDuration !== null ? formatDuration(averageDuration) : '—'} 
          />
          <StatPill 
            label="Avg Rating" 
            value={averageRating !== null ? `${averageRating}/5` : '—'} 
          />
        </div>
        )}

        <SectionCard
          eyebrow="Visual Check-ins"
          title="Progress Photos"
          action={
            <button
              onClick={() => navigate('/client/progress/photos')}
              className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
            >
              Open Gallery →
            </button>
          }
        >
          <div className="grid gap-4 lg:grid-cols-[1.4fr_0.9fr]">
            <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-[radial-gradient(circle_at_top_left,_rgba(249,115,22,0.12),_transparent_36%),linear-gradient(135deg,_rgba(255,255,255,0.94),_rgba(249,250,251,0.9))] p-5 dark:bg-[radial-gradient(circle_at_top_left,_rgba(249,115,22,0.16),_transparent_32%),linear-gradient(135deg,_rgba(255,255,255,0.04),_rgba(255,255,255,0.02))]">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Keep the same pose, distance, and lighting.
                  </p>
                  <p className="mt-1 text-sm text-gray-600 dark:text-slate-400">
                    Photos stay on this device by default and are ready for quick side-by-side comparison.
                  </p>
                </div>
                <Button onClick={() => navigate('/client/progress/photos/capture')}>
                  <Camera size={18} weight="bold" />
                  Take Progress Photo
                </Button>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3">
                {progressPhotos.slice(0, 3).map((photo) => (
                  <button
                    key={photo.id}
                    onClick={() => navigate('/client/progress/photos')}
                    className="group overflow-hidden rounded-2xl border border-gray-200/80 dark:border-white/10"
                  >
                    <div className="aspect-[3/4] overflow-hidden">
                      <img
                        src={photo.imageDataUrl}
                        alt={`Progress photo from ${formatProgressPhotoDate(photo.createdAt)}`}
                        className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
                      />
                    </div>
                    <div className="bg-white/80 px-3 py-2 text-left dark:bg-black/20">
                      <p className="text-xs font-medium text-gray-900 dark:text-white">
                        {formatProgressPhotoDate(photo.createdAt, { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  </button>
                ))}

                {progressPhotos.length === 0 && (
                  <div className="col-span-3 flex min-h-40 flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 px-4 text-center dark:border-white/15">
                    <ImagesSquare size={28} className="text-gray-400 dark:text-slate-500" weight="duotone" />
                    <p className="mt-3 text-sm font-medium text-gray-900 dark:text-white">No progress photos yet</p>
                    <p className="mt-1 text-sm text-gray-600 dark:text-slate-400">
                      Use the guided camera to start a consistent photo log.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 p-4 dark:bg-white/5">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-slate-400">Saved</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">{progressPhotos.length}</p>
              </div>
              <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 p-4 dark:bg-white/5">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-slate-400">Compare Ready</p>
                <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
                  {comparePair ? 'First vs latest available' : 'Take 2 photos'}
                </p>
              </div>
              <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 p-4 dark:bg-white/5">
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-slate-400">Latest Check-in</p>
                <p className="mt-2 text-lg font-semibold text-gray-900 dark:text-white">
                  {progressPhotos[0] ? formatProgressPhotoDate(progressPhotos[0].createdAt) : 'None yet'}
                </p>
              </div>
            </div>
          </div>
        </SectionCard>

        {/* Recent Activity */}
        <SectionCard
          eyebrow="Activity"
          title="Recent Sessions"
          action={
            <span className="text-xs text-gray-600 dark:text-slate-400">
              Last 5 sessions
            </span>
          }
        >
          <div className="space-y-3">
            {sessions.length === 0 && (
              <p className="text-gray-600 dark:text-slate-400 text-sm">
                No sessions completed yet. Start your first workout!
              </p>
            )}
            {sessions
              .sort(
                (a, b) =>
                  (b.startedAt as Timestamp).toMillis() -
                  (a.startedAt as Timestamp).toMillis()
              )
              .slice(0, 5)
              .map((session) => {
                const sessionDuration = session.completedAt
                  ? getDurationMinutes(session.startedAt, session.completedAt)
                  : null
                return (
                  <button
                    key={session.id}
                    onClick={() => navigate(`/session/${session.id}`)}
                    className="w-full text-left p-4 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 hover:bg-gray-100 dark:hover:bg-white/10 transition"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {session.workoutSnapshot.name}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-slate-400">
                          {(session.startedAt as Timestamp)
                            .toDate()
                            .toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                          {' at '}
                          {(session.startedAt as Timestamp)
                            .toDate()
                            .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                          {session.workoutSnapshot.items?.length || 0} exercises
                          {sessionDuration !== null && (
                            <> · {formatDuration(sessionDuration)}</>
                          )}
                        </p>
                        {session.rating && session.rating > 0 && (
                          <div className="mt-1">
                            <StarRating value={session.rating} readonly size="sm" />
                          </div>
                        )}
                      </div>
                      <span className={`text-xs uppercase tracking-wide font-semibold px-2 py-1 rounded ${
                        session.status === 'completed'
                          ? 'bg-success-50 dark:bg-success-500/10 text-success-700 dark:text-success-400'
                          : session.status === 'in_progress'
                          ? 'bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400'
                          : 'bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-slate-400'
                      }`}>
                        {session.status}
                      </span>
                    </div>
                  </button>
                )
              })}
          </div>
        </SectionCard>

        {/* Coming Soon */}
        <SectionCard
          eyebrow="Future Features"
          title="Coming Soon"
        >
          <div className="p-4 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5">
            <p className="font-medium text-gray-900 dark:text-white mb-1">Achievements</p>
            <p className="text-sm text-gray-600 dark:text-slate-400">
              Unlock badges and milestones as you progress
            </p>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
